import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import NetworkCheckOutlinedIcon from "@mui/icons-material/NetworkCheckOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { createDatabaseServer, testDatabaseServer, updateDatabaseServer } from "../../../actions";
import { notice as tones } from "../../../theme/businessFlowsTheme";
import { Notice, Refusal } from "../../workspaces/WorkspaceUi";
import { ADDRESS_PATTERN, encryptionHint } from "./databaseForms";

const EMPTY = { name: "", uri: "", user: "neo4j", password: "" };

// Add a Neo4j server, or change one: test the connection, then save. The
// password is sent once and never shown again.
export default function ServerDialog({ open, server, onClose, onSaved }) {
  const editing = Boolean(server);
  const [form, setForm] = useState(EMPTY);
  const [reveal, setReveal] = useState(false);
  const [test, setTest] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(server ? { name: server.name, uri: server.uri, user: server.user, password: "" } : EMPTY);
    setReveal(false);
    setTest(null);
    setError("");
    setSaving(false);
  }, [open, server]);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const uri = form.uri.trim();
  const signature = `${uri}\n${form.user.trim()}\n${form.password}`;
  const addressProblem =
    uri && !ADDRESS_PATTERN.test(uri) ? "Start with neo4j://, neo4j+s://, neo4j+ssc://, bolt://, bolt+s:// or bolt+ssc://, then the host." : "";
  // AuraDB over a plain address fails with the driver's routing dump: say so
  // here, with the fix one click away, rather than spending a test on it.
  const encryption = encryptionHint(uri);
  const addressOrUserChanged = !editing || uri !== server.uri || form.user.trim() !== server.user;
  const credentialsChanged = addressOrUserChanged || form.password !== "";
  const complete = form.name.trim() && uri && !addressProblem && form.user.trim() && (editing || form.password);
  const tested = Boolean(test && !test.running && test.ok && test.signature === signature);
  const canSave = Boolean(complete) && (!credentialsChanged || tested) && !saving;

  const runTest = async () => {
    setTest({ running: true });
    const input = { uri, user: form.user.trim(), password: form.password };
    const res = await testDatabaseServer(editing ? { id: server.id, ...input } : input);
    setTest(res?.success ? { ...res.content, signature } : { ok: false, message: res?.msg || "The test didn't run.", signature });
  };

  const save = async () => {
    setSaving(true);
    setError("");
    const input = { name: form.name.trim(), uri, user: form.user.trim(), password: form.password };
    const res = editing ? await updateDatabaseServer(server.id, input) : await createDatabaseServer(input);
    setSaving(false);
    if (res?.success) onSaved(res.msg || "Server saved.");
    else setError(res?.msg || "Couldn't save the server.");
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ className: "bfx", sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{editing ? `Edit ${server.name}` : "Connect a Neo4j server"}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {editing
            ? "Change how Business Flows reaches this server. Leave the password empty to keep the current one."
            : "A server in your cloud or on-premise. Its databases can then be connected to workspaces."}
        </Typography>
        <Stack spacing={2}>
          <TextField label="Name" value={form.name} onChange={set("name")} placeholder="e.g. Acme on-premise" autoFocus inputProps={{ maxLength: 80 }} />
          <TextField
            label="Address"
            value={form.uri}
            onChange={set("uri")}
            placeholder="neo4j+s://abc123.databases.neo4j.io"
            error={Boolean(addressProblem)}
            helperText={addressProblem || "neo4j://, neo4j+s://, neo4j+ssc://, bolt://, bolt+s:// or bolt+ssc://"}
            autoComplete="off"
          />
          {encryption && (
            <Notice
              tone={tones.wait}
              icon={<LockOutlinedIcon fontSize="small" />}
              title={encryption.message}
              next={encryption.next}
              action={
                <Button size="small" variant="outlined" onClick={() => setForm((current) => ({ ...current, uri: encryption.fix }))}>
                  {encryption.fixLabel}
                </Button>
              }
            />
          )}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label="User" value={form.user} onChange={set("user")} autoComplete="off" />
            <TextField
              label="Password"
              value={form.password}
              onChange={set("password")}
              type={reveal ? "text" : "password"}
              placeholder={editing ? "Unchanged" : ""}
              required={editing && credentialsChanged}
              helperText={
                editing && addressOrUserChanged
                  ? "Type the password to use a different address"
                  : editing && form.password !== ""
                  ? "The new password is tested before it's saved"
                  : undefined
              }
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setReveal((v) => !v)} aria-label={reveal ? "Hide password" : "Show password"}>
                      {reveal ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          {test && !test.running && (
            <Notice
              tone={test.ok ? tones.good : tones.fix}
              icon={test.ok ? <CheckCircleRoundedIcon fontSize="small" /> : <ErrorOutlineRoundedIcon fontSize="small" />}
              title={test.message}
            >
              {test.ok && (
                <Box sx={{ mt: 1 }}>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {test.edition && <Chip size="small" label={test.edition === "enterprise" ? "Enterprise edition" : "Community edition"} />}
                    {(test.databases || []).slice(0, 12).map((name) => (
                      <Chip key={name} size="small" variant="outlined" label={name} />
                    ))}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    {test.canCreate
                      ? "Databases can be created on this server from the console."
                      : "Databases can't be created here from the console - connect existing ones instead."}
                  </Typography>
                </Box>
              )}
            </Notice>
          )}
          {credentialsChanged && complete && !tested && !(test && test.running) && (
            <Typography variant="caption" color="text.secondary">
              Test the connection before saving.
            </Typography>
          )}
          {error && <Refusal onClose={() => setError("")}>{error}</Refusal>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, flexWrap: "wrap", gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={test?.running ? <CircularProgress size={14} color="inherit" /> : <NetworkCheckOutlinedIcon />}
          disabled={!complete || Boolean(test?.running)}
          onClick={runTest}
          sx={{ mr: "auto" }}
        >
          Test connection
        </Button>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSave} onClick={save} startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
          {editing ? "Save" : "Connect server"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
